import { Component, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { DemoRequestInquiry, Inquiry } from '@app/core/models/inquiries.model';
import { CommonService } from '@app/core/services/common.service';
import { InquiryType } from '@app/core/enums/common.enums';
import { ASSETS } from '@app/core/constants/assets.constants';
import { APP_ROUTES } from '@app/core/constants/routes.constants';
import { Button } from '@shared/components/form-fields/button/button';
import { Captcha } from '@shared/components/form-fields/captcha/captcha';
import { AlertService } from '@app/core/services/alert.service';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'app-request-demo',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterLink, Button, Captcha],
  templateUrl: './request-demo.html',
  styleUrls: ['./request-demo.scss'],
})
export class RequestDemo {
  @ViewChild(Captcha) captchaComponent?: Captcha;
  isLoading: boolean = false;
  ASSETS = ASSETS;
  APP_ROUTES = APP_ROUTES;
  legalLinks = [
    {
      label: 'Privacy Notes',
      route: [APP_ROUTES.GUEST.LEGAL.ROOT, APP_ROUTES.GUEST.LEGAL.PRIVACY_POLICY],
    },
    {
      label: 'Terms of Use',
      route: [APP_ROUTES.GUEST.LEGAL.ROOT, APP_ROUTES.GUEST.LEGAL.TERMS_OF_SERVICE],
    },
    { label: 'Cookies Preference', route: '#' },
    { label: 'Legal', route: '#' },
  ];

  onClickLegalLink(link: any): void {
    const routePath = '/' + link.route.join('/');
    const fullUrl = `${window.location.origin}${routePath}`;
    window.open(fullUrl, '_blank');
  }

  teamSizes = [
    { value: '  1-10', label: '1-10' },
    { value: '11-50', label: '11-50' },
    { value: '51-100', label: '51-100' },
    { value: '101-500', label: '101-500' },
    { value: '501-1000', label: '501-1000' },
    { value: '1001-5000', label: '1001-5000' },
    { value: '5001-10000', label: '5001-10000' },
    { value: '10001-50000', label: '10001-50000' },
    { value: '50001-100000', label: '50001-100000' },
  ];

  demoForm = new FormGroup({
    name: new FormControl('', [Validators.required]),
    lastName: new FormControl(''),
    email: new FormControl('', [Validators.required, Validators.email]),
    phoneNumber: new FormControl('', [Validators.required,
    Validators.minLength(6),
    Validators.maxLength(15),
    ]),
    organizationName: new FormControl('', [Validators.required]),
    message: new FormControl(''),
    teamSize: new FormControl(''),
    recaptcha: new FormControl('', [Validators.required]),
  });

  constructor(
    private commonService: CommonService,
    private alertService: AlertService
  ) { }

  onSubmit(): void {
    if (this.isLoading) return;

    const isValidExcludingCaptcha = this.isFormValidExcludingCaptcha();
    if (!isValidExcludingCaptcha) {
      this.demoForm.markAllAsTouched();
      return;
    }

    const captchaValue = this.demoForm.get('recaptcha')?.value;
    if (!captchaValue) {
      this.captchaComponent?.execute();
      this.submitInquiry();
      return;
    }
  }

  private submitInquiry(): void {
    this.isLoading = true;
    const formValue = this.demoForm.value;

    // Combine name and lastName
    const fullName = formValue.lastName
      ? `${formValue.name} ${formValue.lastName}`.trim()
      : formValue.name || '';

    const inquiry: Inquiry = {
      name: fullName,
      email: formValue.email || '',
      organizationName: formValue.organizationName || '',
      phoneNumber: formValue.phoneNumber || '',
      message: formValue.message || '',
      inquiryType: InquiryType.demo,
      teamSize: formValue.teamSize || '',
    };

    this.commonService.inquiriesLead(inquiry as DemoRequestInquiry)
      .pipe(finalize(() => {
        this.isLoading = false;
        this.captchaComponent?.resetCaptcha();
        this.demoForm.get('recaptcha')?.setValue('');
      })).subscribe({
        next: (result) => {
          if (result.success) {
            this.demoForm.reset();
            this.alertService.showSuccessPopup(
              'Great!',
              'Your Demo Request Has Been Submitted Successfully. We will get back to you within 24 hours.',
              {
                text: 'Continue',
                action: () => {
                  this.alertService.clearAllPopupAlerts();
                },
              },
              {
                text: 'Close',
                action: () => {
                  console.log('Cancel clicked');
                  this.alertService.clearAllPopupAlerts();
                },
              }
            );
          }
        },
        error: (err) => {
          console.log('Demo Request Failed:', err);
          this.alertService.showErrorPopup(
            'Oh, No',
            err.error.message,
            {
              text: 'Try Again',
              action: () => {
                console.log('Try Again clicked');
                this.alertService.clearAllPopupAlerts();
              },
            },
            {
              text: 'Close',
              action: () => {
                console.log('Cancel clicked');
                this.alertService.clearAllPopupAlerts();
              },
            }
          );
        },
      });
  }

  private isFormValidExcludingCaptcha(): boolean {
    return Object.entries(this.demoForm.controls).every(([key, control]) => {
      if (key === 'recaptcha') return true;
      return control.valid;
    });
  }

  getFieldError(fieldName: string): string {
    const field = this.demoForm.get(fieldName);
    if (field?.hasError('required') && field?.touched) {
      return 'This field is required';
    }
    if (field?.hasError('email') && field?.touched) {
      return 'Please enter a valid email';
    }
    if (field?.hasError('minlength') && field?.touched) {
      return 'Please enter a valid phone number with at least 6 digits';
    }
    if (field?.hasError('maxlength') && field?.touched) {
      return 'Please enter a valid phone number with at most 15 digits';
    }
    return '';
  }

  onPhoneNumberInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value.replace(/[^0-9]/g, '');
    this.demoForm.get('phoneNumber')?.setValue(value);
  }


}
